import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import useContentProtection from '../../hooks/useContentProtection';
import PreviewBanner from '../../components/rpat/PreviewBanner';
import { 
  BookOpen, 
  Play, 
  Clock, 
  CheckCircle2,
  Lock,
  ChevronRight,
  ArrowLeft,
  Target,
  Mic,
  Keyboard
} from 'lucide-react';

const RPATPhase = () => {
  const { phaseId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(null);
  const [topics, setTopics] = useState([]);
  const [isPhasePreview, setIsPhasePreview] = useState(false);

  const fetchPhase = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/courses/rpat-001/topics');
      
      // Find the phase and its topics (topics are nested inside the phase object)
      const phases = res.data.phases || [];
      const phaseIndex = phases.findIndex(p => p.id === phaseId);
      const phaseData = phases[phaseIndex];
      const phaseTopics = phaseData?.topics || [];
      
      // Check if this is a preview (phase is locked)
      // Phase is unlocked if:
      // 1. User is admin, OR
      // 2. This is the first phase (index 0), OR
      // 3. Previous phase has 100% progress (all topics completed)
      const isAdmin = user?.role === 'admin';
      let previousPhaseComplete = phaseIndex === 0;
      
      if (!previousPhaseComplete && phaseIndex > 0) {
        const prevPhase = phases[phaseIndex - 1];
        // Check progress_percentage OR fallback to counting completed topics
        const prevProgress = prevPhase?.progress_percentage ?? 0;
        const prevCompletedCount = prevPhase?.completed_topics_count ?? 
          (prevPhase?.topics?.filter(t => t.status === 'completed')?.length ?? 0);
        const prevTotalCount = prevPhase?.total_topics_count ?? prevPhase?.topics?.length ?? 0;
        
        // Previous phase is complete if progress is 100% OR all topics are completed
        previousPhaseComplete = prevProgress >= 100 || 
          (prevTotalCount > 0 && prevCompletedCount >= prevTotalCount);
      }
      
      setIsPhasePreview(!isAdmin && !previousPhaseComplete);
      
      setPhase(phaseData);
      setTopics(phaseTopics);
    } catch (err) {
      console.error('Failed to fetch phase:', err);
    } finally {
      setLoading(false);
    }
  }, [api, phaseId, user?.role]);

  useEffect(() => {
    fetchPhase();
  }, [fetchPhase]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-800">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-amber-100 text-amber-800">In Progress</Badge>;
      case 'locked':
        return <Badge variant="secondary">Locked</Badge>;
      default:
        return <Badge variant="outline">Not Started</Badge>;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32" />
          <div className="space-y-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  const completedTopics = topics.filter(t => t.status === 'completed').length;
  const progressPct = topics.length > 0 ? (completedTopics / topics.length) * 100 : 0;

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{phase?.name}</h1>
            <p className="text-muted-foreground">{phase?.description}</p>
          </div>
        </div>

        {/* Progress Card */}
        <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Phase Progress</p>
                <p className="text-2xl font-bold text-cyan-700">
                  {completedTopics} / {topics.length} topics
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-cyan-600">{Math.round(progressPct)}%</p>
              </div>
            </div>
            <Progress value={progressPct} className="h-3" />
          </CardContent>
        </Card>

        {/* Preview Mode Banner */}
        {isPhasePreview && (
          <PreviewBanner 
            message="Complete the previous phase to unlock full access to these topics."
          />
        )}

        {/* Topics List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Topics</h2>
          
          {topics.map((topic, index) => {
            const isAdmin = user?.role === 'admin';
            // In preview mode, all topics appear as "preview"
            const isLocked = isPhasePreview || (!isAdmin && topic.status === 'locked');
            const isCompleted = topic.status === 'completed';
            const isInProgress = topic.status === 'in_progress';
            
            const handleTopicClick = () => {
              if (isPhasePreview) {
                toast.info('Complete the previous phase to access this topic');
                return;
              }
              if (isLocked && !isAdmin) {
                toast.info('Complete the previous topic first');
                return;
              }
              navigate(`/rpat/lesson/${topic.id}`);
            };
            
            return (
              <Card 
                key={topic.id}
                className={`transition-all hover:shadow-md cursor-pointer ${
                  isLocked ? 'opacity-70 grayscale-[20%]' : ''
                } ${isCompleted ? 'border-emerald-200 bg-emerald-50/30' : ''}`}
                onClick={handleTopicClick}
              >
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    {/* Topic Number */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                      isCompleted 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : isInProgress 
                          ? 'bg-cyan-100 text-cyan-700'
                          : isLocked
                            ? 'bg-muted text-muted-foreground'
                            : 'bg-cyan-50 text-cyan-700'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle2 className="h-6 w-6" />
                      ) : isLocked ? (
                        <Lock className="h-5 w-5" />
                      ) : (
                        topic.code
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{topic.title}</h3>
                        {getStatusBadge(topic.status)}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {topic.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {topic.duration_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          <Mic className="h-3 w-3" />
                          Voice lesson
                        </span>
                        {topic.typing_test_number && (
                          <span className="flex items-center gap-1">
                            <Keyboard className="h-3 w-3" />
                            Typing test #{topic.typing_test_number}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Progress/Action */}
                    <div className="flex items-center gap-4">
                      {isCompleted && topic.mock_test_passed && (
                        <div className="text-right">
                          <p className="text-sm font-medium text-emerald-700">
                            {topic.mock_test_score}%
                          </p>
                          <p className="text-xs text-muted-foreground">Test Score</p>
                        </div>
                      )}
                      
                      {!isLocked && (
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  {/* Progress Bar for In-Progress */}
                  {isInProgress && topic.progress_percentage > 0 && (
                    <div className="mt-3 ml-16">
                      <Progress value={topic.progress_percentage} className="h-1.5" />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </MainLayout>
  );
};

export default RPATPhase;
